import type {MouseEvent} from 'react';
import {Link} from 'react-router-dom';

/** True for absolute http(s) or protocol-relative URLs. */
export const isExternalCommandSearchHref = (to: string): boolean =>
    /^(https?:)?\/\//i.test(to);

/**
 * True when the click should be left to the browser (new tab, modified click, etc.).
 */
export const isCommandSearchModifiedClick = (event: MouseEvent): boolean =>
    event.metaKey
    || event.ctrlKey
    || event.shiftKey
    || event.altKey
    || event.button !== 0;

export interface CommandSearchLinkProps {
    to?: string;
    target?: string;
    rel?: string;
}

/**
 * Props for rendering a result/action as a real link (RR Link or &lt;a&gt;).
 * Returns an empty object when `to` is absent.
 */
export const getCommandSearchLinkComponentProps = (
    link: CommandSearchLinkProps,
): Record<string, unknown> => {
    if (!link.to) {
        return {};
    }

    const external = isExternalCommandSearchHref(link.to);
    return {
        component: external ? 'a' : Link,
        ...(external ? {href: link.to} : {to: link.to}),
        ...(link.target ? {target: link.target} : {}),
        ...(link.rel ? {rel: link.rel} : {}),
    };
};

/**
 * Handles primary vs modified / _blank clicks for link-backed command-search controls.
 * Primary clicks always preventDefault when `to` is set so selection runs once
 * (SPA push or _blank open in selectResult); modified clicks leave the real href alone.
 */
export const handleCommandSearchLinkClick = (
    event: MouseEvent,
    link: CommandSearchLinkProps,
): {runHandler: boolean; preventDefault: boolean} => {
    if (isCommandSearchModifiedClick(event)) {
        return {runHandler: false, preventDefault: false};
    }

    if (link.to) {
        return {runHandler: true, preventDefault: true};
    }

    return {runHandler: true, preventDefault: false};
};
